// Screenshots live in public/tour and are regenerated with
// apps/customer/scripts/capture-tour.py (keep file names in sync).
// `device: 'phone'` slides are portrait phone captures shown in a phone frame.
export const SECONDS_PER_SLIDE = 20;

export const TOUR_TRACKS = [
  {
    key: 'pos',
    label: 'Behind the Counter',
    slides: [
      {
        image: '/tour/pos-01-orders.jpg',
        title: 'Every order on one board',
        caption: 'Walk-in, online, texted-in, pickup, and salon or gym orders land on the same screen. Filter by where it came from or where it is, and see what is due, what is paid, and which rack the bag is on.',
        unique: 'Move an order to the next step with one tap. No menus to hunt through.',
      },
      {
        image: '/tour/pos-02-order-details.jpg',
        title: 'The whole order in one drawer',
        caption: 'Customer, items with per-unit prices, weights, photos, payment, and history in one place. Set the rack slot, print a receipt, order tag, or bag tags, save a card on file or prepaid account funds, and add services with one-tap favorites.',
        unique: 'If the customer chose a price per bag, staff cannot quietly switch it to per pound. Pickup stays blocked until it is paid.',
      },
      {
        image: '/tour/pos-03-quick-pos.jpg',
        title: 'Ring up a walk-in',
        caption: 'Built for the counter: tap the service, change the quantity, add a coupon, add a tip, and pick the day they need it. Cash, card, card reader, or pay later.',
        unique: 'Bundle pricing (like “each additional comforter $20”) is applied automatically, and the cash drawer pops on cash sales with every open logged.',
      },
      {
        image: '/tour/pos-04-mobile-order.jpg',
        device: 'phone',
        title: 'Scan the bag tag, work from your phone',
        caption: 'Every bag tag has a code. Staff scan it, enter their PIN, and get a checklist: weigh it, photograph what came in, then washing, drying, and folding.',
        unique: 'When the customer arrives, scanning shows the rack slot in big letters, any balance still due, and a one-tap “Mark Picked Up.”',
      },
      {
        image: '/tour/pos-05-services.jpg',
        title: 'Your pricing, your rules',
        caption: 'Price by the pound, by the bag, or by the piece. Set a minimum weight. Delivery can be free, one flat fee, or by the mile. Try an address and see the fee the customer will pay.',
        unique: 'The counter and the website use the same prices, so staff and customers see the same number.',
      },
      {
        image: '/tour/pos-06-route-planner.jpg',
        title: 'Driver routes, planned for you',
        caption: "Today's pickups and deliveries show on a map, and late stops are marked. Pick the drivers and assign the routes in two clicks.",
        unique: 'Customers get a text with a live link to track their driver as their stop gets close — fewer "where is my laundry?" calls.',
      },
      {
        image: '/tour/pos-07-driver-app.jpg',
        device: 'phone',
        title: 'A driver app that runs the day',
        caption: 'Drivers see their stops for the day, follow the optimized route, and claim unassigned pickups with one tap. Pickup and delivery photos are saved to the order as proof.',
        unique: 'No separate driver software — the driver app, the counter, and the customer’s tracking page share the same order.',
      },
      {
        image: '/tour/pos-08-commercial.jpg',
        title: 'Win commercial accounts',
        caption: 'Salons, gyms, and Airbnb hosts get one billing account with as many locations as they need. Approve sign-ups, pause accounts, and see every location in one place.',
        unique: 'Consolidated daily or monthly invoices to the billing contact, broken down by location — the paperwork big accounts expect.',
      },
      {
        image: '/tour/pos-09-engagement.jpg',
        title: 'Texts you do not have to remember',
        caption: 'Start from messages we wrote: welcome, please review us, we miss you, and a thank-you. Customers are grouped for you: new, started a booking and stopped, quiet, and regulars.',
        unique: 'The unfinished-booking text, the "we miss you" text, holiday texts, and send-a-friend credit are included in the monthly price.',
      },
      {
        image: '/tour/pos-10-message-templates.jpg',
        title: 'Every text and email, in your words',
        caption: 'Rewrite every text and email: order confirmed, ready, driver on the way, and the rest. The customer’s name and pickup time fill in on their own. Turn any message off.',
        unique: 'Customers choose how they hear from you (text, email, or both), and every message respects their choice automatically.',
      },
      {
        image: '/tour/pos-11-ai-tracking.jpg',
        title: 'Photo count — 1¢ per piece',
        caption: 'Photos of the clothes are counted and checked against the fold, so a missing sock is flagged before the bag goes out.',
        unique: 'A picture of what came in ends "you lost my shirt." Add it here, or add it to the laundry software you already use.',
        illustrative: true,
      },
      {
        image: '/tour/pos-12-dashboard.jpg',
        title: 'Know your numbers',
        caption: 'Sales, how many orders, the average order, and where each order stands. Reports for sales tax, tips, and expenses, and you can download them.',
        unique: 'If you use another tool, it can be notified when an order is created, paid, or finished.',
        illustrative: true,
      },
      {
        image: '/tour/pos-13-timeclock.jpg',
        title: 'Time clock and staff checklists',
        caption: 'Staff clock in and out with a PIN on a shared tablet. Daily opening and closing checklists are checked off by name, and managers see the history.',
        unique: 'No separate time-tracking or task app — hours, tips, checklists, and roles live in the same system as your orders.',
      },
    ],
  },
  {
    key: 'customer',
    label: "Your Customers' Side",
    slides: [
      {
        image: '/tour/cust-01-site.jpg',
        title: 'A branded website on day one',
        caption: 'Your logo, colors, a promo, and your address, live the day you sign up. Customers book a pickup in a couple of taps. A chat on the site answers hours and prices.',
        unique: 'No web designer. You change the site from the settings screen.',
      },
      {
        image: '/tour/cust-02-services.jpg',
        title: 'Clear pricing customers trust',
        caption: 'Prices are grouped: by the pound, by the bag, comforters. They come from the price list you set, so the website matches the counter.',
        unique: 'Change a price once. It updates on the website, at the counter, and on invoices.',
      },
      {
        image: '/tour/cust-03-ordering.jpg',
        title: 'Booking in under a minute',
        caption: 'Customers pick services, a pickup day and window, and one-time or recurring service. The estimated return date is shown before they confirm.',
        unique: 'Weekly, every-other-week, and monthly pickups, plus a monthly pound plan, turn a one-time customer into a regular.',
        illustrative: true,
      },
      {
        image: '/tour/cust-04-tracking.jpg',
        title: 'Customers always know where their laundry is',
        caption: 'A clear timeline shows every step from pickup to delivery, with the price of each item spelled out and a plain payment status — including a one-tap fix if their card needs updating.',
        unique: 'A text at each step means fewer "where is my laundry?" calls.',
        illustrative: true,
      },
    ],
  },
];
